import React from "react";
import { GraduationCap, Clock, PlayCircle, User, Check, Loader2, Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Reveal, useSEO } from "../components/site/Reveal";
import Newsletter from "../components/site/Newsletter";
import { useCurrency } from "../context/CurrencyContext";
import CurrencySelector from "../components/site/CurrencySelector";
import CourseEditorModal from "../components/site/CourseEditorModal";
import PaystackButton from "../components/site/PaystackButton";
import { useMember } from "../context/MemberAuthContext";
import { useAuth } from "../context/AuthContext";
import { IMAGES } from "../lib/images";

function CourseCard({ c, isAdmin, onEnroll, onEdit, onDelete }) {
  const { format } = useCurrency();
  return (
    <div className="group relative h-full flex flex-col rounded-2xl border border-cream/12 bg-night-surface overflow-hidden transition-colors hover:border-gold/40" data-testid={`course-card-${c.id}`}>
      {isAdmin && (
        <div className="absolute top-3 right-3 z-10 flex gap-2">
          <button onClick={() => onEdit(c)} data-testid={`course-edit-${c.id}`} title="Edit" className="grid place-items-center w-9 h-9 rounded-full bg-night/80 text-cream hover:bg-forest backdrop-blur transition-colors"><Pencil className="w-4 h-4" /></button>
          <button onClick={() => onDelete(c)} data-testid={`course-delete-${c.id}`} title="Delete" className="grid place-items-center w-9 h-9 rounded-full bg-night/80 text-cream hover:bg-red-500 backdrop-blur transition-colors"><Trash2 className="w-4 h-4" /></button>
        </div>
      )}
      {/* Patterned banner */}
      <div className="relative h-28 overflow-hidden shrink-0">
        <img src={c.banner_image || IMAGES.textileGeometric} alt="" className="w-full h-full object-cover" loading="lazy" />
      </div>

      <div className="flex flex-col flex-1 p-6">
        <h3 className="font-serif text-2xl text-cream leading-tight mb-1">{c.title}</h3>
        {!c.published && isAdmin && <span className="inline-block self-start mb-2 text-xs bg-cream/10 text-cream/60 rounded px-2 py-0.5">Hidden</span>}
        <p className="text-2xl font-serif text-gold mb-3" data-testid={`course-price-${c.id}`}>{format(c.price)}</p>
        {c.subtitle && <p className="text-cream/80 text-[15px] font-medium leading-snug mb-3">{c.subtitle}</p>}
        {(c.learn || []).length > 0 && (
          <p className="text-cream/55 text-sm leading-relaxed mb-4">
            <span className="text-cream/80 font-semibold">Things you'll learn: </span>
            {(c.learn || []).join(", ")}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-cream/60 mt-auto pt-2 border-t border-cream/10">
          {c.level && <span className="inline-flex items-center gap-1.5"><User className="w-3.5 h-3.5 text-gold" /> {c.level}</span>}
          {c.lectures && <span className="inline-flex items-center gap-1.5"><PlayCircle className="w-3.5 h-3.5 text-gold" /> {c.lectures}</span>}
          {c.duration && <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-gold" /> {c.duration}</span>}
        </div>
      </div>

      <div className="p-4 pt-0">
        <button onClick={() => onEnroll(c)} data-testid={`course-enroll-${c.id}`} className="w-full inline-flex items-center justify-center gap-2 bg-[#C0562B] text-cream font-semibold py-3 rounded-lg hover:bg-[#a9491f] transition-colors">
          Enroll
        </button>
      </div>
    </div>
  );
}

function EnrollModal({ course, onClose }) {
  const { format, isConverted } = useCurrency();
  const [email, setEmail] = React.useState("");
  const { member } = useMember();
  React.useEffect(() => { if (member?.email) setEmail((prev) => prev || member.email); }, [member]);
  const [busy, setBusy] = React.useState("");
  const [ppReady, setPpReady] = React.useState(false);
  const paypalRef = React.useRef(null);

  React.useEffect(() => {
    api.get("/paypal/config").then(({ data }) => {
      if (!data.configured || !data.client_id) return;
      if (window.paypal) { setPpReady(true); return; }
      const s = document.createElement("script");
      s.src = `https://www.paypal.com/sdk/js?client-id=${data.client_id}&currency=USD`;
      s.onload = () => setPpReady(true);
      document.body.appendChild(s);
    }).catch(() => {});
  }, []);

  React.useEffect(() => {
    if (!ppReady || !window.paypal || !paypalRef.current) return;
    paypalRef.current.innerHTML = "";
    window.paypal.Buttons({
      style: { color: "gold", shape: "pill", label: "pay", height: 45 },
      createOrder: async () => {
        const { data } = await api.post("/courses/enroll", { course_id: course.id, origin_url: window.location.origin, email, method: "paypal" });
        return data.paypal_order_id;
      },
      onApprove: async (data) => {
        const { data: res } = await api.post(`/shop/paypal-capture/${data.orderID}`);
        if (res.payment_status === "paid") { window.location.href = "/courses/success?paid=paypal"; }
        else { toast.error("Payment could not be completed."); }
      },
    }).render(paypalRef.current);
  }, [ppReady, course, email]);

  const stripeCheckout = async () => {
    setBusy("stripe");
    try {
      const { data } = await api.post("/courses/enroll", { course_id: course.id, origin_url: window.location.origin, email, method: "stripe" });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout.");
      setBusy("");
    }
  };

  return (
    <div className="fixed inset-0 z-[70] grid place-items-center p-4" data-testid="enroll-modal" onClick={onClose}>
      <div className="absolute inset-0 bg-night/85 backdrop-blur-sm" />
      <div className="relative w-full max-w-md rounded-3xl border border-gold/25 bg-night-surface p-7" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} data-testid="enroll-modal-close" aria-label="Close" className="absolute top-4 right-4 grid place-items-center w-9 h-9 rounded-full bg-night/60 border border-cream/20 text-cream hover:bg-gold hover:text-night transition-colors"><X className="w-4 h-4" /></button>
        <div className="flex items-center gap-2 mb-2"><GraduationCap className="w-5 h-5 text-gold" /><p className="uppercase tracking-[0.2em] text-xs text-gold-soft">Enroll</p></div>
        <h3 className="font-serif text-2xl text-cream leading-tight mb-1">{course.title}</h3>
        <p className="text-3xl font-serif text-gold mb-1" data-testid="enroll-price">{format(course.price)}</p>
        {isConverted && <p className="text-xs text-gold/80 mb-4">You'll be charged ${Number(course.price).toFixed(2)} USD — other currencies are shown at today's rate.</p>}
        {!isConverted && <div className="mb-4" />}

        <label className="block text-sm text-cream/70 mb-1.5">Email (for your receipt & access)</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="enroll-email-input" className="w-full rounded-lg bg-night border border-cream/20 px-4 py-2.5 text-cream placeholder:text-cream/40 mb-4 focus:outline-none focus:border-gold" />

        <button onClick={stripeCheckout} disabled={busy === "stripe"} data-testid="enroll-stripe-button" className="w-full inline-flex items-center justify-center gap-2 bg-gold text-night font-semibold py-3 rounded-full hover:bg-gold-light transition-colors disabled:opacity-60">
          {busy === "stripe" ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : "Pay with Card (Stripe)"}
        </button>
        <div className="text-center text-xs text-cream/40 my-3">or</div>
        <div ref={paypalRef} data-testid="enroll-paypal-buttons" />
        <PaystackButton
          variant="dark"
          initialize={async (cur) => {
            if (!email) { toast.error("Please enter your email first."); throw new Error("email required"); }
            const { data } = await api.post("/courses/enroll", { course_id: course.id, origin_url: window.location.origin, email, method: "paystack", currency: cur });
            return data.authorization_url;
          }}
        />
        <div className="mt-4 flex justify-center"><CurrencySelector variant="dark" /></div>
      </div>
    </div>
  );
}

export default function Courses() {
  useSEO(
    "Learning Portal — Yorùbá Language Courses",
    "Learn to speak Yorùbá at your own pace. Beginner, Intermediate and Premium course packages plus focused lessons on the alphabet, numbers, greetings, verbs and more."
  );
  const { user } = useAuth();
  const isAdmin = user && user.role === "admin";
  const [courses, setCourses] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [enrolling, setEnrolling] = React.useState(null);
  const [editing, setEditing] = React.useState(null); // null | 'new' | course

  const load = React.useCallback(() => {
    setLoading(true);
    api.get(isAdmin ? "/admin/courses" : "/courses")
      .then((r) => setCourses(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isAdmin]);

  React.useEffect(() => { load(); }, [load]);

  const removeCourse = async (c) => {
    if (!window.confirm(`Delete "${c.title}"?`)) return;
    try { await api.delete(`/admin/courses/${c.id}`); toast.success("Course deleted."); load(); }
    catch { toast.error("Delete failed."); }
  };

  return (
    <div className="bg-night text-cream min-h-screen">
      {/* HERO */}
      <header className="relative grain-overlay">
        <div className="absolute inset-0">
          <img src={IMAGES.storytellingPortrait} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-night/85 via-night/80 to-night" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 pt-28 pb-16 text-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 mb-4"><GraduationCap className="w-4 h-4 text-gold" /><p className="uppercase tracking-[0.25em] text-xs text-gold-soft">Learning Portal</p></div>
            <h1 className="font-serif text-5xl lg:text-6xl leading-[1.05] mb-5" data-testid="courses-title">Courses</h1>
            <p className="text-cream/75 text-lg leading-relaxed max-w-2xl mx-auto">
              Whether you're a complete newbie or you want to improve your fluency in the Yorùbá
              language, we have different well‑developed course packages you can take at your own
              pace and master the language.
            </p>
          </Reveal>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 pb-24 relative z-10">
        <div className="flex items-center justify-between gap-3 mb-6 mt-8">
          <CurrencySelector variant="dark" />
          {isAdmin && (
            <button onClick={() => setEditing("new")} data-testid="courses-add-button" className="inline-flex items-center gap-2 bg-forest text-cream font-semibold px-5 py-2.5 rounded-full hover:bg-forest-light transition-colors">
              <Plus className="w-4 h-4" /> Add Course
            </button>
          )}
        </div>

        {loading ? (
          <p className="text-cream/50 py-16 text-center">Loading courses…</p>
        ) : courses.length === 0 ? (
          <p className="text-cream/50 py-16 text-center" data-testid="courses-empty">No courses available yet — please check back soon.</p>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {courses.map((c, i) => (
              <Reveal key={c.id} delay={(i % 3) * 0.05}>
                <CourseCard c={c} isAdmin={isAdmin} onEnroll={setEnrolling} onEdit={setEditing} onDelete={removeCourse} />
              </Reveal>
            ))}
          </div>
        )}
      </div>

      {enrolling && <EnrollModal course={enrolling} onClose={() => setEnrolling(null)} />}
      {editing && <CourseEditorModal course={editing} onClose={() => setEditing(null)} onSaved={load} />}

      <Newsletter />
    </div>
  );
}
